import { Head, Link } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  ArrowLeft,
  Edit,
  Trash2,
  TrendingUp,
  DollarSign,
  Target,
  BarChart3,
  PieChart,
  Plus,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { portfoliosPath, editPortfolioPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Portfolios",
    href: portfoliosPath(),
  },
  {
    title: "Portfolio Details",
    href: "#",
  },
]

interface PortfolioPosition {
  id: number
  symbol: string
  shares: number
  current_price: number
  average_cost: number
  total_cost: number
  current_value: number
  unrealized_gain_loss: number
  unrealized_gain_loss_percentage: number
}

interface Portfolio {
  id: number
  name: string
  total_value: number
  total_cost_basis: number
  total_return: number
  total_return_percentage: number
  total_dividends: number
  created_at: string
  updated_at: string
  portfolio_positions: PortfolioPosition[]
}

interface Dividend {
  id: number
  amount: number
  payment_date: string
  ex_dividend_date: string
  reinvested: boolean
}

interface RecentDividend {
  id: number
  symbol: string
  dividends: Dividend[]
}

interface PerformanceMetrics {
  total_return: number
  annualized_return: number
  volatility: number
  sharpe_ratio: number
  max_drawdown: number
}

interface RiskMetrics {
  volatility: number
  sharpe_ratio: number
  max_drawdown: number
  beta: number
  alpha: number
}

interface PortfolioShowProps {
  portfolio: Portfolio
  performance_metrics: PerformanceMetrics
  risk_metrics: RiskMetrics
  recent_dividends: RecentDividend[]
}

export default function PortfolioShow({
  portfolio,
  performance_metrics,
  recent_dividends,
}: PortfolioShowProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatPercentage = (percentage: number | null | undefined) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) {
      return "0.00%"
    }
    // Additional safety check in case percentage is not a number
    const numPercentage =
      typeof percentage === "number"
        ? percentage
        : parseFloat(String(percentage))
    if (isNaN(numPercentage)) {
      return "0.00%"
    }
    return `${numPercentage >= 0 ? "+" : ""}${numPercentage.toFixed(2)}%`
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMMM dd, yyyy")
  }

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a")
  }

  const getReturnColor = (return_percentage: number | null | undefined) => {
    if (
      return_percentage === null ||
      return_percentage === undefined ||
      isNaN(return_percentage)
    ) {
      return "text-gray-600"
    }
    if (return_percentage > 0) return "text-green-600"
    if (return_percentage < 0) return "text-red-600"
    return "text-gray-600"
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${portfolio.name} - Portfolio Details`} />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href={portfoliosPath()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Portfolios
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{portfolio.name}</h1>
              <p className="text-muted-foreground">
                Portfolio performance and position details
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link
                href={`/portfolios/${portfolio.id}/portfolio_positions/new`}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Position
              </Link>
            </Button>
            <Button asChild>
              <Link href={editPortfolioPath(portfolio.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Portfolio
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this portfolio? This action cannot be undone.",
                  )
                ) {
                  // Handle delete - this would typically use Inertia's delete method
                  window.location.href = portfoliosPath()
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio.total_value || 0)}
              </div>
              <p className="text-muted-foreground text-xs">
                Current portfolio value
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Return
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio.total_return || 0)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatPercentage(portfolio.total_return_percentage ?? null)}{" "}
                total return
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cost Basis</CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio.total_cost_basis || 0)}
              </div>
              <p className="text-muted-foreground text-xs">
                Total amount invested
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Dividends</CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio.total_dividends || 0)}
              </div>
              <p className="text-muted-foreground text-xs">
                Total dividends received
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Portfolio Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Positions List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio Positions</CardTitle>
              </CardHeader>
              <CardContent>
                {portfolio.portfolio_positions.length > 0 ? (
                  <div className="space-y-4">
                    {portfolio.portfolio_positions.map((position) => (
                      <div
                        key={position.id}
                        className="flex items-center justify-between rounded-lg border p-4"
                      >
                        <div>
                          <div className="font-medium">{position.symbol}</div>
                          <div className="text-muted-foreground text-sm">
                            {position.shares || 0} shares @{" "}
                            {formatCurrency(position.average_cost || 0)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatCurrency(position.current_value || 0)}
                          </div>
                          <div
                            className={`text-sm ${getReturnColor(position.unrealized_gain_loss_percentage ?? null)}`}
                          >
                            {formatCurrency(position.unrealized_gain_loss || 0)}{" "}
                            (
                            {formatPercentage(
                              position.unrealized_gain_loss_percentage ?? null,
                            )}
                            )
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <PieChart className="text-muted-foreground mx-auto h-12 w-12" />
                    <h3 className="mt-2 text-sm font-medium">
                      No positions yet
                    </h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Add your first position to start tracking investments.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Performance Metrics */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    Total Return
                  </span>
                  <span className="text-sm font-medium">
                    {formatCurrency(performance_metrics?.total_return || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    Annualized Return
                  </span>
                  <span className="text-sm font-medium">
                    {formatPercentage(
                      performance_metrics?.annualized_return ?? null,
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    Volatility
                  </span>
                  <span className="text-sm font-medium">
                    {formatPercentage(performance_metrics?.volatility ?? null)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    Sharpe Ratio
                  </span>
                  <span className="text-sm font-medium">
                    {performance_metrics?.sharpe_ratio?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    Max Drawdown
                  </span>
                  <span className="text-sm font-medium">
                    {formatPercentage(
                      performance_metrics?.max_drawdown ?? null,
                    )}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Dividends */}
        {recent_dividends.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Dividends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recent_dividends.slice(0, 5).map((position) =>
                  position.dividends.slice(0, 3).map((dividend) => (
                    <div
                      key={dividend.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div>
                        <div className="font-medium">{position.symbol}</div>
                        <div className="text-muted-foreground text-sm">
                          {dividend.payment_date
                            ? formatDate(dividend.payment_date)
                            : "N/A"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          {formatCurrency(dividend.amount || 0)}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {dividend.reinvested ? "Reinvested" : "Cash"}
                        </div>
                      </div>
                    </div>
                  )),
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-muted-foreground mb-1 text-sm font-medium">
                Created
              </div>
              <div className="text-sm">
                {portfolio.created_at
                  ? formatDateTime(portfolio.created_at)
                  : "N/A"}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1 text-sm font-medium">
                Last Updated
              </div>
              <div className="text-sm">
                {portfolio.updated_at
                  ? formatDateTime(portfolio.updated_at)
                  : "N/A"}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1 text-sm font-medium">
                Portfolio ID
              </div>
              <div className="font-mono text-sm">#{portfolio.id || "N/A"}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1 text-sm font-medium">
                Positions
              </div>
              <div className="text-sm">
                {portfolio.portfolio_positions?.length ?? 0}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
